import {
    HTMLAttributes,
    MouseEvent,
    ReactNode,
    useCallback,
    useContext,
} from 'react'
import { twMerge } from 'tailwind-merge'

import { createToggleableContext, useToggleableContext } from 'hooks'

interface AccordionProps {
    readonly open?: boolean
    readonly className?: string
    readonly children?: ReactNode
}

const AccordionContext = createToggleableContext(false)

function Accordion({ open, className, children }: AccordionProps): JSX.Element {
    const context = useToggleableContext(open)

    return (
        <AccordionContext.Provider value={context}>
            <div className={className}>{children}</div>
        </AccordionContext.Provider>
    )
}

interface AccordionContentProps {
    readonly open?: boolean
    readonly className?: string
    readonly children?: ReactNode
}

function AccordionContent({
    open,
    className,
    children,
}: AccordionContentProps): JSX.Element {
    const _open = open ?? useContext(AccordionContext)

    return (
        <div
            className={twMerge(
                'grid grid-rows-[0fr] transition-all duration-300',
                _open && 'grid-rows-[1fr]',
                className
            )}
        >
            <div className="overflow-hidden">{children}</div>
        </div>
    )
}

interface AccordionToggleProps extends HTMLAttributes<HTMLDivElement> {}

function AccordionToggle({
    onClick,
    ...props
}: AccordionToggleProps): JSX.Element {
    const context = useContext(AccordionContext)
    const onToggle = useCallback((e: MouseEvent<HTMLDivElement>) => {
        context.toggle()
        onClick?.(e)
    }, [])

    return <div {...props} onClick={onToggle} />
}

Accordion.Content = AccordionContent
Accordion.Context = AccordionContext
Accordion.Toggle = AccordionToggle

export { Accordion }
